<template>
  <div class="media-message">
    <!-- Loading state -->
    <div v-if="loading" class="loading-state py-3">
      <div class="flex items-center justify-center">
        <svg class="animate-spin h-5 w-5 text-blue-500 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span>Cargando {{ mediaType === 'audio' ? 'audio' : 'video' }}...</span>
      </div>
    </div>
    
    <!-- Error state -->
    <div v-else-if="errorMessage" class="error-state text-red-500 py-3">
      <p class="text-center">{{ errorMessage }}</p>
      <button @click="retryLoading" class="mt-2 text-blue-500 underline">Reintentar</button>
    </div>
    
    <!-- Chunked media (with loading indicator) -->
    <div v-else-if="isChunkedMedia && !isFullyLoaded" class="chunked-media-loading p-4 bg-gray-700 rounded-lg text-white text-center">
      <div class="flex flex-col items-center justify-center space-y-2">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 text-blue-400 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
        <p class="font-medium">{{ mediaType === 'video' ? 'Video' : 'Audio' }} ({{ formatSize(sizeMB) }})</p>
        <p class="text-sm opacity-75">Cargando fragmentos {{ loadedChunks }}/{{ totalChunks }}</p>
        <button @click="assembleMediaChunks" class="mt-2 bg-blue-600 px-4 py-1 rounded-full text-sm hover:bg-blue-700">
          Cargar ahora
        </button>
      </div>
    </div>
    
    <!-- Reproductor de Audio -->
    <div v-else-if="mediaType === 'audio' && effectiveMediaUrl" class="audio-player">
      <div class="w-full rounded-lg bg-black bg-opacity-5 p-1">
        <audio 
          controls 
          class="w-full" 
          :src="effectiveMediaUrl" 
          @play="trackMediaPlay"
          @error="handleMediaError"
          :type="getAudioType()"
          preload="metadata"
        ></audio>
      </div>
      <div class="text-xs text-right mt-1 opacity-70 flex justify-between">
        <span v-if="mediaError" class="text-red-500">Error: {{ mediaError }}</span>
        <span>{{ formatDuration(duration) }}</span>
      </div>
      
      <!-- Botón de reproducción alternativo por si falla el reproductor nativo -->
      <button 
        v-if="mediaError"
        @click="downloadMedia" 
        class="mt-2 w-full py-1 px-2 bg-blue-500 hover:bg-blue-600 text-white rounded text-xs flex items-center justify-center"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
        Descargar audio
      </button>
    </div>
    
    <!-- Reproductor de Video -->
    <div v-else-if="mediaType === 'video' && effectiveMediaUrl" class="video-player">
      <video 
        controls
        playsinline
        class="w-full rounded-lg" 
        :src="effectiveMediaUrl" 
        @play="trackMediaPlay"
        @error="handleMediaError"
        :type="getVideoType()"
        preload="metadata"
      >
        <p>Tu navegador no soporta la reproducción de video.</p>
      </video>
      <div class="text-xs text-right mt-1 opacity-70 flex justify-between">
        <span v-if="mediaError" class="text-red-500">Error: {{ mediaError }}</span>
        <span>{{ formatDuration(duration) }}</span>
      </div>
      
      <!-- Botón de reproducción alternativo por si falla el reproductor nativo -->
      <button 
        v-if="mediaError"
        @click="downloadMedia" 
        class="mt-2 w-full py-1 px-2 bg-blue-500 hover:bg-blue-600 text-white rounded text-xs flex items-center justify-center"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
        Descargar video
      </button>
    </div>
    
    <!-- Si hay transcripción, mostrarla debajo del reproductor -->
    <div v-if="transcription" class="transcription mt-2 text-sm opacity-80">
      <p>{{ transcription }}</p>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { doc, getDoc, collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db } from '@/firebase';

export default {
  name: 'MediaMessage',
  props: {
    mediaUrl: {
      type: String,
      required: false,
      default: ''
    },
    mediaData: {
      type: String,
      required: false,
      default: ''
    },
    mediaChunks: {
      type: Array,
      required: false,
      default: () => []
    },
    totalChunks: {
      type: Number,
      required: false,
      default: 0
    },
    mimeType: {
      type: String,
      required: false,
      default: ''
    },
    encoding: {
      type: String,
      required: false,
      default: 'base64'
    },
    mediaType: {
      type: String,
      required: true,
      validator: (value) => ['audio', 'video'].includes(value)
    },
    mediaId: {
      type: String,
      required: false,
      default: ''
    },
    isLarge: {
      type: Boolean,
      required: false,
      default: false
    },
    sizeMB: {
      type: Number,
      required: false,
      default: 0
    },
    storeMethod: {
      type: String,
      required: false,
      default: 'inline'
    },
    transcription: {
      type: String,
      required: false,
      default: ''
    },
    duration: {
      type: Number,
      required: false,
      default: 0
    }
  },
  
  setup(props) {
    const loading = ref(true);
    const errorMessage = ref('');
    const mediaError = ref('');
    const actualMediaUrl = ref('');
    const isFullyLoaded = ref(false);
    const loadedChunks = ref(0);
    
    // Variable local para almacenar los datos - sin exponer
    let mediaDataValue = '';
    
    // Function to load media data from Firestore
    const loadMediaFromFirestore = async () => {
      if (!props.mediaId) {
        errorMessage.value = "No se encontró ID de media";
        loading.value = false;
        return false;
      }
      
      try {
        loading.value = true;
        console.log(`Cargando media con ID: ${props.mediaId}`);
        
        // Obtener el documento principal de media
        const mediaDocRef = doc(db, "media", props.mediaId);
        const mediaDoc = await getDoc(mediaDocRef);
        
        if (!mediaDoc.exists()) {
          console.error(`Documento de media no encontrado: ${props.mediaId}`);
          errorMessage.value = "No se encontraron datos multimedia";
          loading.value = false;
          return false;
        }
        
        // Obtener metadata del media
        const mediaDocData = mediaDoc.data();
        console.log("Media metadata cargada:", {
          tipo: mediaDocData.mediaType,
          mimeType: mediaDocData.mimeType,
          storeMethod: mediaDocData.storeMethod
        });
        
        if (mediaDocData.storeMethod === 'inline') {
          // Media almacenado directamente
          mediaDataValue = mediaDocData.mediaData;
          createDataUrl();
          return true;
        } else if (mediaDocData.storeMethod === 'chunked') {
          // Media almacenado en chunks
          console.log(`Cargando chunks para media ${props.mediaId}, total: ${mediaDocData.totalChunks}`);
          
          // Buscar los chunks
          const chunksQuery = query(
            collection(db, "media"),
            where("mediaId", "==", props.mediaId),
            orderBy("chunkIndex")
          );
          
          const chunksSnapshot = await getDocs(chunksQuery);
          const chunks = [];
          
          chunksSnapshot.forEach(doc => {
            const chunkData = doc.data();
            if (chunkData.data) {
              chunks[chunkData.chunkIndex] = chunkData.data;
            }
          });
          
          console.log(`Chunks cargados: ${chunks.length} de ${mediaDocData.totalChunks} esperados`);
          
          if (chunks.length === 0) {
            // Si no hay chunks, intentar usar inline data
            if (mediaDocData.mediaData) {
              mediaDataValue = mediaDocData.mediaData;
              createDataUrl();
              return true;
            } else {
              errorMessage.value = "No se encontraron datos multimedia";
              loading.value = false;
              return false;
            }
          }
          
          // Ensamblar todos los chunks
          mediaDataValue = chunks.join('');
          createDataUrl();
          return true;
        }
        
        return false;
      } catch (error) {
        console.error("Error cargando media:", error);
        errorMessage.value = `Error al cargar multimedia: ${error.message}`;
        loading.value = false;
        return false;
      }
    };
    
    // Function to create a data URL from Base64 data
    const createDataUrl = () => {
      if (mediaDataValue) {
        // Determine the correct MIME type, defaulting to more compatible formats
        const mimeType = props.mimeType || 
                        (props.mediaType === 'audio' ? 'audio/webm' : 'video/mp4');
        
        console.log(`Creating data URL for media type: ${props.mediaType}, MIME: ${mimeType}`);
        
        // Create the data URL with proper encoding
        const dataUrl = `data:${mimeType};${props.encoding},${mediaDataValue}`;
        actualMediaUrl.value = dataUrl;
        loading.value = false;
        isFullyLoaded.value = true;
        return true;
      }
      
      // Si tenemos mediaData en los props, usarlo (fallback)
      if (props.mediaData) {
        const mimeType = props.mimeType || 
                       (props.mediaType === 'audio' ? 'audio/webm' : 'video/mp4');
        
        const dataUrl = `data:${mimeType};${props.encoding},${props.mediaData}`;
        actualMediaUrl.value = dataUrl;
        loading.value = false;
        isFullyLoaded.value = true;
        return true;
      }
      
      if (props.mediaUrl) {
        actualMediaUrl.value = props.mediaUrl;
        loading.value = false;
        isFullyLoaded.value = true;
        return true;
      }
      
      return false;
    };
    
    // Function to assemble media chunks into a complete file
    const assembleMediaChunks = async () => {
      // Show loading indicator
      loading.value = true;
      
      try {
        // Si tenemos un ID de media, cargarlo de Firestore
        if (props.mediaId) {
          console.log(`Cargando media chunks para ${props.mediaId}`);
          await loadMediaFromFirestore();
          return;
        }
        
        // Fallback al método antiguo
        if (!props.mediaChunks || props.mediaChunks.length === 0) {
          errorMessage.value = "No hay fragmentos de media para ensamblar";
          return;
        }
        
        // Combine all chunks into one string
        const fullData = props.mediaChunks.join('');
        
        // Create a data URL from the complete data
        const mimeType = props.mimeType || 
                      (props.mediaType === 'audio' ? 'audio/webm' : 'video/mp4');
        const dataUrl = `data:${mimeType};${props.encoding},${fullData}`;
        
        // Update the media URL
        actualMediaUrl.value = dataUrl;
        isFullyLoaded.value = true;
        loadedChunks.value = props.totalChunks;
      } catch (error) {
        console.error('Error assembling media chunks:', error);
        errorMessage.value = "Error al ensamblar los fragmentos de media";
      } finally {
        loading.value = false;
      }
    };
    
    // Retry loading the media if there was an error
    const retryLoading = async () => {
      errorMessage.value = '';
      loading.value = true;
      
      // Si es una referencia a Firestore, intentar cargar de nuevo
      if (props.mediaId && props.type === 'reference') {
        const result = await loadMediaFromFirestore();
        if (!result && !createDataUrl()) {
          errorMessage.value = "No se pudo cargar el contenido multimedia";
        }
      } else if (!createDataUrl()) {
        errorMessage.value = "No se encontró información multimedia";
      }
    };
    
    // Format duration (seconds) to MM:SS
    const formatDuration = (seconds) => {
      if (!seconds) return '';
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.floor(seconds % 60);
      return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    };
    
    // Format size in MB to a readable string
    const formatSize = (mb) => {
      if (!mb) return '';
      if (mb < 1) return `${(mb * 1024).toFixed(0)} KB`;
      return `${mb.toFixed(1)} MB`;
    };
    
    // Track when media is played (could be used for analytics)
    const trackMediaPlay = () => {
      console.log(`Media played: ${props.mediaId} (${props.mediaType})`);
      // Si se reproduce, limpiamos cualquier error anterior
      mediaError.value = '';
    };
    
    // Detectar el tipo de audio más adecuado para el navegador
    const getAudioType = () => {
      const isAppleDevice = /iPhone|iPad|iPod|Mac|Safari/i.test(navigator.userAgent);
      // Safari e iOS prefieren mp4/aac
      return isAppleDevice 
        ? (props.mimeType?.includes('mp4') ? props.mimeType : 'audio/mp4')
        : (props.mimeType || 'audio/webm');
    };
    
    // Detectar el tipo de video más adecuado para el navegador
    const getVideoType = () => {
      const isAppleDevice = /iPhone|iPad|iPod|Mac|Safari/i.test(navigator.userAgent);
      // Safari e iOS prefieren mp4/h264
      return isAppleDevice 
        ? (props.mimeType?.includes('mp4') ? props.mimeType : 'video/mp4')
        : (props.mimeType || 'video/webm');
    };
    
    // Función para descargar el archivo multimedia
    const downloadMedia = () => {
      // Calculamos la URL efectiva en el momento
      const downloadUrl = actualMediaUrl.value || props.mediaUrl || '';
      if (!downloadUrl) return;
      
      // Crear un enlace temporal para descargar
      const downloadLink = document.createElement('a');
      downloadLink.href = downloadUrl;
      
      // Determinar nombre de archivo
      const timestamp = new Date().getTime();
      const extension = props.mediaType === 'audio' ? 
        (/iPhone|iPad|iPod|Mac|Safari/i.test(navigator.userAgent) ? 'm4a' : 'webm') : 
        (/iPhone|iPad|iPod|Mac|Safari/i.test(navigator.userAgent) ? 'mp4' : 'webm');
      
      downloadLink.download = `${props.mediaType}_${timestamp}.${extension}`;
      
      // Añadir al DOM temporalmente, hacer clic y luego eliminar
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      
      console.log(`Archivo descargado: ${downloadLink.download}`);
    };
    
    // Manejar errores en la reproducción
    const handleMediaError = (event) => {
      console.error('Error en reproducción de media:', event);
      
      // Verificar el tipo de error
      const mediaElement = event.target;
      const errorCode = mediaElement.error ? mediaElement.error.code : 0;
      
      // Códigos de error: https://developer.mozilla.org/en-US/docs/Web/API/MediaError/code
      switch (errorCode) {
        case 1: // MEDIA_ERR_ABORTED
          mediaError.value = "Reproducción cancelada";
          break;
        case 2: // MEDIA_ERR_NETWORK
          mediaError.value = "Error de red";
          break;
        case 3: // MEDIA_ERR_DECODE
          mediaError.value = "Error de formato";
          break;
        case 4: // MEDIA_ERR_SRC_NOT_SUPPORTED
          mediaError.value = "Formato no soportado";
          // Sugerir alternativa en Safari/iOS
          if (/iPhone|iPad|iPod|Mac|Safari/i.test(navigator.userAgent)) {
            console.log("Dispositivo Apple detectado, sugiriendo descargar el archivo");
          }
          break;
        default:
          mediaError.value = "Error desconocido";
      }
    };
    
    // Load media on component mount
    onMounted(async () => {
      console.log("MediaMessage montado, tipo:", props.mediaType, "ID:", props.mediaId);
      
      // Si es una referencia a media almacenado en Firestore
      if (props.mediaId && props.type === 'reference') {
        console.log("Cargando media desde Firestore");
        const result = await loadMediaFromFirestore();
        if (!result) {
          console.log("Fallback a usar datos inline");
          if (!createDataUrl()) {
            errorMessage.value = "No se pudo cargar el contenido multimedia";
          }
        }
      } else {
        // Fallback al método tradicional
        console.log("Usando datos locales");
        if (!createDataUrl()) {
          errorMessage.value = "No se encontró información multimedia";
        }
      }
    });
    
    // Clean up resources when component is unmounted
    onBeforeUnmount(() => {
      // No cleanup needed for data URLs
    });
    
    // Compute if this is a chunked media that needs assembly
    const isChunkedMedia = props.storeMethod === 'chunked' && props.totalChunks > 1;
    
    return {
      loading,
      errorMessage,
      mediaError,
      actualMediaUrl,
      retryLoading,
      formatDuration,
      formatSize,
      trackMediaPlay,
      handleMediaError,
      getAudioType,
      getVideoType,
      downloadMedia,
      isChunkedMedia,
      isFullyLoaded,
      loadedChunks,
      assembleMediaChunks,
      loadMediaFromFirestore,
      createDataUrl
    };
  },
  
  computed: {
    effectiveMediaUrl() {
      return this.actualMediaUrl || this.mediaUrl || '';
    }
  }
};
</script>

<style scoped>
audio, video {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.transcription {
  font-style: italic;
}

.loading-state, .error-state {
  background-color: rgba(0, 0, 0, 0.05);
  border-radius: 0.5rem;
  text-align: center;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}
</style>